import { useApolloClient } from '@apollo/react-hooks';
import { useReactiveVar } from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';
import type { Observable } from 'zen-observable-ts';
import { PROVIDER_BOOKED_SESSIONS_SUBSCRIPTION } from 'api/graphql/v2/subscriptions/Providers';
import { providerResourcesPrevVar } from 'utils/cache/filters';
import {
  IWeeklyHoursData,
  IWeeklySessionsData
} from 'model/v2/providerCapacity';
import { ICalendarResource } from 'model/calendar/filters';

type ZenSubscription = { unsubscribe: () => void };

type ApolloLike = {
  subscribe: (options: { query: any; variables?: any }) => Observable<any>;
};

interface ProviderBookedSessionsUpdate {
  providerId: number;
  bookedSessions?: IWeeklySessionsData;
  bookedHours?: IWeeklyHoursData;
  targetSessions?: IWeeklySessionsData;
}

type Listener = (update: ProviderBookedSessionsUpdate) => void;

interface ProviderEntry {
  sub: ZenSubscription;
  listeners: Set<Listener>;
  teardownTimer: ReturnType<typeof setTimeout> | null;
  lastSeenAt?: number;
}

const registry = new Map<number, ProviderEntry>();

// Short TTL to suppress the StrictMode double-mount / double-delivery we
// occasionally see when the transport replays the same frame.
const DEDUPE_TTL_MS = 300;

const subscribeToProvider = (
  client: ApolloLike,
  providerId: number,
  listener: Listener
) => {
  const existing = registry.get(providerId);
  if (existing) {
    if (existing.teardownTimer !== null) {
      clearTimeout(existing.teardownTimer);
      existing.teardownTimer = null;
    }
    existing.listeners.add(listener);
    return;
  }

  const listeners = new Set<Listener>();
  listeners.add(listener);

  console.log(
    '[ProviderBookedSessions] starting singleton Apollo subscription for providerId:',
    providerId
  );

  const sub = client
    .subscribe({
      query: PROVIDER_BOOKED_SESSIONS_SUBSCRIPTION,
      variables: { providerId }
    })
    .subscribe({
      next: ({
        data
      }: {
        data?: { providerBookedSessions?: ProviderBookedSessionsUpdate };
      }) => {
        const update = data?.providerBookedSessions;
        if (!update || !update.providerId) return;

        const entry = registry.get(providerId);
        if (!entry) return;

        // Payload dedupe — the transport occasionally double-delivers.
        const now = Date.now();
        if (
          entry.lastSeenAt !== undefined &&
          now - entry.lastSeenAt < DEDUPE_TTL_MS
        ) {
          console.log(
            '[ProviderBookedSessions] duplicate suppressed for providerId:',
            providerId
          );
          return;
        }
        entry.lastSeenAt = now;

        entry.listeners.forEach(l => {
          try {
            l(update);
          } catch (e) {
            console.error(
              '[ProviderBookedSessions] listener error for providerId:',
              providerId,
              e
            );
          }
        });
      },
      error: (err: any) => {
        console.error(
          '[ProviderBookedSessions] subscription error for providerId:',
          providerId,
          err
        );
      }
    });

  registry.set(providerId, {
    sub,
    listeners,
    teardownTimer: null
  });
};

const unsubscribeFromProvider = (providerId: number, listener: Listener) => {
  const entry = registry.get(providerId);
  if (!entry) return;
  entry.listeners.delete(listener);
  if (entry.listeners.size > 0) return;
  if (entry.teardownTimer !== null) return;

  // Deferred teardown so StrictMode's unmount → remount cycle doesn't churn
  // the server-side subscriber row.
  entry.teardownTimer = setTimeout(() => {
    const current = registry.get(providerId);
    if (!current) return;
    if (current.listeners.size > 0) {
      current.teardownTimer = null;
      return;
    }
    console.log(
      '[ProviderBookedSessions] tearing down singleton subscription for providerId:',
      providerId
    );
    current.sub.unsubscribe();
    registry.delete(providerId);
  }, 0);
};

/**
 * Derive the set of DT provider IDs currently displayed on the Planner.
 * These are the only providers we open subscriptions for.
 */
const toDTProviderIds = (providers: ICalendarResource[]): number[] => {
  const ids = new Set<number>();
  for (const p of providers) {
    if (p.speciality?.department !== 'DT') continue;

    const providerIdNum =
      typeof p.id === 'string' ? parseInt(p.id, 10) : (p.id as number);
    const baseIdNum = p.baseId ? parseInt(p.baseId, 10) : NaN;

    // Prefer baseId (the underlying provider) if available; otherwise fall
    // back to id. This mirrors the lookup previously done client-side in
    // updateProviderCache.
    const chosen = !isNaN(baseIdNum) ? baseIdNum : providerIdNum;
    if (!isNaN(chosen)) ids.add(chosen);
  }
  return Array.from(ids);
};

/**
 * Update the providerResourcesPrevVar reactive var with the new booked sessions,
 * booked hours and target sessions for a specific provider.
 */
const updateProviderCache = (update: ProviderBookedSessionsUpdate) => {
  const providers = providerResourcesPrevVar();

  let foundProvider = false;
  const updatedProviders = providers.map((provider: ICalendarResource) => {
    const providerIdNum =
      typeof provider.id === 'string' ? parseInt(provider.id) : provider.id;
    const baseIdNum = provider.baseId ? parseInt(provider.baseId) : null;

    if (
      providerIdNum === update.providerId ||
      baseIdNum === update.providerId
    ) {
      foundProvider = true;
      return {
        ...provider,
        providerCapacity: {
          ...provider.providerCapacity,
          providerBookedSessions: update.bookedSessions,
          providerBookedHours:
            update.bookedHours || provider.providerCapacity?.providerBookedHours,
          providerTargetSessions:
            update.targetSessions ||
            provider.providerCapacity?.providerTargetSessions
        }
      };
    }
    return provider;
  });

  if (!foundProvider) {
    // Most likely the DT view changed between publish and apply — safe to drop.
    return;
  }

  providerResourcesPrevVar(updatedProviders as ICalendarResource[]);
};

export const useProviderBookedSessionsSubscription = () => {
  const client = (useApolloClient() as unknown) as ApolloLike;
  const providers = useReactiveVar<ICalendarResource[]>(
    providerResourcesPrevVar
  );

  // Stable, sorted list of DT provider IDs so the effect only re-runs when
  // the actual membership changes, not just reference identity.
  const dtProviderIds = useMemo(() => {
    const ids = toDTProviderIds(providers);
    ids.sort((a, b) => a - b);
    return ids;
  }, [providers]);

  // Memoize the signature so we don't tear-down/rebuild on every render.
  const dtProviderIdsKey = dtProviderIds.join(',');

  // Keep a stable listener so we can unregister the exact same reference.
  const listenerRef = useRef<Listener>();
  if (!listenerRef.current) {
    listenerRef.current = (update: ProviderBookedSessionsUpdate) => {
      console.log('[ProviderBookedSessions] applying update:', update);
      updateProviderCache(update);
    };
  }

  useEffect(() => {
    if (dtProviderIds.length === 0) return;
    const listener = listenerRef.current!;

    dtProviderIds.forEach(id => subscribeToProvider(client, id, listener));

    return () => {
      dtProviderIds.forEach(id => unsubscribeFromProvider(id, listener));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, dtProviderIdsKey]);

  return { error: undefined as unknown };
};
